import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, Lock, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { onboardingPasswordChange } from "@/lib/onboarding-api";
import { toast } from "sonner";

// PUB-04b — Forced password change after first sign-in with the GCBC-XXXXXX
// temp password. Rendered inside StandaloneShell (same auth chrome as /login).
export default function PasswordChangePage() {
  const navigate = useNavigate();
  const [cred, setCred] = useState({ email: "", password: "" });
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("gcbc:tempCred");
    if (!raw) {
      // No temp credentials in session → user landed here directly.
      navigate("/login", { replace: true });
      return;
    }
    try { setCred(JSON.parse(raw)); }
    catch { navigate("/login", { replace: true }); }
  }, [navigate]);

  // Simple strength rules — surfaces as three checks.
  const checks = {
    length:    newPassword.length >= 8,
    notTemp:   newPassword.length > 0 && newPassword !== cred.password,
    matches:   confirmPassword.length > 0 && confirmPassword === newPassword,
  };
  const canSubmit = checks.length && checks.notTemp && checks.matches && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onboardingPasswordChange(cred.email, cred.password, newPassword);
      sessionStorage.removeItem("gcbc:tempCred");
      toast.success("Password set", { description: "You're signed in." });
      navigate("/welcome", { replace: true });
    } catch (err) {
      setError(err.message || "Couldn't update your password. Try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-7" data-testid="password-change-page">
      <div>
        <div className="eyebrow mb-3 text-brand-700">First sign-in</div>
        <h1 className="font-brand text-[36px] leading-[1.1] tracking-[-0.02em] text-ink font-bold">Set a new password.</h1>
        <p className="mt-3 text-body text-ink-muted leading-relaxed">
          Your temporary password expires after this step. Pick something memorable — you&rsquo;ll use it every time you sign in.
        </p>
      </div>

      <div className="bg-surface-alt border border-line rounded-md px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-caption text-ink-subtle">Signed in as</div>
          <div className="text-body-sm text-ink font-medium font-mono">{cred.email || "…"}</div>
        </div>
        <Lock className="size-4 text-ink-hint" />
      </div>

      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <Label className="text-eyebrow uppercase text-ink-muted mb-1.5 block">New password</Label>
          <div className="relative">
            <Input
              type={showPwd ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoFocus required minLength={8}
              data-testid="new-password-input"
              className="h-12 text-body bg-surface border-line pr-11"
            />
            <button type="button" onClick={() => setShowPwd((s) => !s)} aria-label={showPwd ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 size-8 grid place-items-center text-ink-hint hover:text-ink rounded-md hover:bg-surface-alt transition-colors">
              {showPwd ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        </div>

        <div>
          <Label className="text-eyebrow uppercase text-ink-muted mb-1.5 block">Confirm new password</Label>
          <Input
            type={showPwd ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required minLength={8}
            data-testid="confirm-password-input"
            className="h-12 text-body bg-surface border-line"
          />
        </div>

        {/* Live strength check */}
        <ul className="space-y-1.5 text-caption" data-testid="password-checks">
          <Check ok={checks.length}  label="At least 8 characters" />
          <Check ok={checks.notTemp} label="Different from your temporary password" />
          <Check ok={checks.matches} label="Both fields match" />
        </ul>

        {error && (
          <div className="flex items-start gap-2 px-3.5 py-2.5 bg-red-50 border border-red-200 rounded-md text-caption text-red-700">
            <AlertCircle className="size-3.5 mt-0.5 shrink-0" />{error}
          </div>
        )}

        <Button type="submit" disabled={!canSubmit} className="w-full h-12 bg-brand-700 hover:bg-brand-800 text-body-sm font-semibold text-white" data-testid="set-password-submit-btn">
          {submitting ? <><Loader2 className="size-4 mr-2 animate-spin" /> Saving…</> : <>Save password &amp; continue &rarr;</>}
        </Button>
      </form>

      <div className="border-t border-line pt-5 flex items-start gap-2 text-caption text-ink-hint leading-relaxed">
        <Lock className="size-3.5 mt-0.5 shrink-0" />
        Stored as a salted hash. We never see your password in plain text.
      </div>
    </div>
  );
}

function Check({ ok, label }) {
  return (
    <li className={`flex items-center gap-2 ${ok ? "text-emerald-700" : "text-ink-hint"}`}>
      <CheckCircle2 className={`size-3.5 ${ok ? "text-emerald-700" : "text-ink-hint/50"}`} />
      {label}
    </li>
  );
}
